<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CmsElementText } from '@shopware/composables'
import { vInterpolate } from '#imports'

const props = defineProps<{
    content: CmsElementText
}>()

const { sanitizeHtml } = useSanitization()

const textContent = computed(() => {
    return props.content?.data?.content || ''
})

const sanitizedContent = computed(() => {
    return sanitizeHtml(textContent.value, {
        removeElements: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select', 'option']
    })
})

// Collapsible functionality
const isCollapsible = computed(() => {
    return props.content?.config?.collapsible?.value === true
})

const { t } = useI18n()

const collapsibleButtonText = computed(() => {
    return props.content?.config?.collapsibleButtonText?.value as string || t('cms.text.showMore')
})

const isExpanded = ref(false)

const showContent = () => {
    isExpanded.value = true
}
</script>

<template>
    <div v-if="textContent" class="cms-element-text-wrapper">
        <!-- Non-collapsible: normal display -->
        <div
            v-if="!isCollapsible"
            v-interpolate
            class="cms-element-text prose"
            v-html="sanitizedContent"
        />

        <!-- Collapsible: hide content until button click -->
        <div v-else class="cms-element-text-collapsible">
            <!-- Show button when collapsed -->
            <div v-if="!isExpanded">
                <FoundationButton
                    color="secondary"
                    size="medium"
                    :aria-label="collapsibleButtonText"
                    @click="showContent"
                >
                    {{ collapsibleButtonText }}
                </FoundationButton>
            </div>

            <!-- Show content when expanded (SEO-friendly: always in DOM) -->
            <div
                v-show="isExpanded"
                v-interpolate
                class="cms-element-text prose"
                v-html="sanitizedContent"
            />
        </div>
    </div>
</template>